import type { TNetworkError } from '@/services/errors';
import type { TSessionPayload } from './auth.adapters';

import { BaseService } from '@/services/api';
import { ERROR_CODES, errorUtils } from '@/services/errors';
import { persistAdmittedSession, withSessionHeaders } from '@/services/supabase';
import { toSvgDataURI } from '@/utils/svgDataUri';
import { AUTH_ROUTES } from '@/constants/auth';
import { logger } from '@/services/logger';

import type {
  TAdminAccount,
  TSignInPayload,
  TStaffSession,
  TTotpEnrollment,
} from './auth.types';

import { toAdminAccount, toStaffSession } from './auth.adapters';

const STAFF_SESSION_PATH = '/auth-staff/session';

const ANONYMOUS_SESSION: TStaffSession = {
  stage: 'anonymous', email: '', subRole: null, factorID: null,
};

const ANONYMOUS_CODES = ['401', ERROR_CODES.unauthorized];
const FORBIDDEN_CODES = ['403', ERROR_CODES.forbidden];

class AuthService extends BaseService {
  protected readonly serviceName = 'authService';

  private enrollment: Promise<TTotpEnrollment | TNetworkError> | null = null;

  public async signIn({
    email,
    password,
  }: TSignInPayload): Promise<TAdminAccount | TNetworkError> {
    const where = this.callSite('signIn');

    const { data, error } = await this.db.auth.signInWithPassword({ email, password });

    if (error) {
      return errorUtils.fromSupabaseAuth(error, where);
    }

    if (!data.user) {
      return errorUtils.createNetworkError('401', undefined, where);
    }

    return toAdminAccount(data.user);
  }

  public async requestPasswordReset(email: string): Promise<undefined | TNetworkError> {
    const where = this.callSite('requestPasswordReset');
    const redirectTo = `${window.location.origin}${AUTH_ROUTES.newPassword}`;

    const { error } = await this.db.auth.resetPasswordForEmail(email, { redirectTo });

    if (error) {
      return errorUtils.fromSupabaseAuth(error, where);
    }

    return undefined;
  }

  public async verifyRecovery(tokenHash: string): Promise<undefined | TNetworkError> {
    const where = this.callSite('verifyRecovery');

    const { error } = await this.db.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' });

    if (error) {
      return errorUtils.fromSupabaseAuth(error, where);
    }

    return undefined;
  }

  public async updatePassword(password: string): Promise<undefined | TNetworkError> {
    const where = this.callSite('updatePassword');

    const { error } = await this.db.auth.updateUser({ password });

    if (error) {
      return errorUtils.fromSupabaseAuth(error, where);
    }

    const { error: signOutError } = await this.db.auth.signOut({ scope: 'global' });

    if (signOutError) {
      logger.warn(`${where} — password changed but sessions were not revoked`, signOutError);
    }

    return undefined;
  }

  public async readStaffSession(): Promise<TStaffSession | TNetworkError> {
    const where = this.callSite('readStaffSession');

    const { data } = await this.db.auth.getSession();

    if (!data.session) {
      return ANONYMOUS_SESSION;
    }

    const email = data.session.user.email ?? '';

    const payload = await this.post<TSessionPayload, Record<string, never>>(
      STAFF_SESSION_PATH,
      {},
      { where: 'readStaffSession', headers: await withSessionHeaders() },
    );

    if (errorUtils.isNetworkError(payload)) {
      if (ANONYMOUS_CODES.includes(payload.code)) {
        return ANONYMOUS_SESSION;
      }

      if (FORBIDDEN_CODES.includes(payload.code)) {
        return {
          stage: 'forbidden', email, subRole: null, factorID: null,
        };
      }

      return payload;
    }

    const session = toStaffSession(payload, email);

    if (!session) {
      return errorUtils.createNetworkError(ERROR_CODES.unknown, undefined, where);
    }

    if (session.stage === 'ready') {
      this.touchLastActive(data.session.user.id);
    }

    return session;
  }

  private touchLastActive(userID: string): void {
    const where = this.callSite('touchLastActive');

    this.db
      .from('staff')
      .update({ last_active_at: new Date().toISOString() })
      .eq('user_id', userID)
      .then(({ error }) => {
        if (error) {
          logger.warn(`${where} — ${error.code}`, error.message);
        }
      });
  }

  public enrollTotp(): Promise<TTotpEnrollment | TNetworkError> {
    if (!this.enrollment) {
      this.enrollment = this.createTotpEnrollment().finally(() => {
        this.enrollment = null;
      });
    }

    return this.enrollment;
  }

  private async createTotpEnrollment(): Promise<TTotpEnrollment | TNetworkError> {
    const where = this.callSite('enrollTotp');

    const { data: factors, error: listError } = await this.db.auth.mfa.listFactors();

    if (listError) {
      return errorUtils.fromSupabaseAuth(listError, where);
    }

    const stale = factors.all.filter(factor => factor.status === 'unverified');

    await Promise.all(stale.map(factor => this.db.auth.mfa.unenroll({ factorId: factor.id })));

    const { data, error } = await this.db.auth.mfa.enroll({ factorType: 'totp' });

    if (error) {
      return errorUtils.fromSupabaseAuth(error, where);
    }

    return {
      factorID: data.id,
      qrCode: toSvgDataURI(data.totp.qr_code),
      secret: data.totp.secret,
    };
  }

  public async resetTotp(): Promise<undefined | TNetworkError> {
    const where = this.callSite('resetTotp');

    const { data: factors, error: listError } = await this.db.auth.mfa.listFactors();

    if (listError) {
      return errorUtils.fromSupabaseAuth(listError, where);
    }

    const results = await Promise.all(
      factors.all.map(factor => this.db.auth.mfa.unenroll({ factorId: factor.id })),
    );

    const failed = results.find(result => result.error);

    if (failed?.error) {
      return errorUtils.fromSupabaseAuth(failed.error, where);
    }

    return undefined;
  }

  public async readVerifiedFactorID(): Promise<string | null | TNetworkError> {
    const where = this.callSite('readVerifiedFactorID');

    const { data, error } = await this.db.auth.mfa.listFactors();

    if (error) {
      return errorUtils.fromSupabaseAuth(error, where);
    }

    return data.totp.find(factor => factor.status === 'verified')?.id ?? null;
  }

  public async verifyTotp(factorID: string, code: string): Promise<undefined | TNetworkError> {
    const where = this.callSite('verifyTotp');

    const { error } = await this.db.auth.mfa.challengeAndVerify({ factorId: factorID, code });

    if (error) {
      return errorUtils.fromSupabaseAuth(error, where);
    }

    persistAdmittedSession();

    return undefined;
  }

  public async signOut(): Promise<undefined | TNetworkError> {
    const where = this.callSite('signOut');

    const { error } = await this.db.auth.signOut();

    if (error) {
      return errorUtils.fromSupabaseAuth(error, where);
    }

    return undefined;
  }
}

const authService = new AuthService();

export {
  authService,
};
